import React, { useEffect, useId, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { ExternalLink, Megaphone, ShieldCheck, UserRound, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { isSiteAdmin } from '../utils/isSiteAdmin';
import type { ForumAdminContact, ForumBase } from '../types/forum';
import { resolveForumAdmins, resolveForumControllers } from '../utils/forumAdmins';

export type ForumAdminMetaFields = Pick<
  ForumBase,
  | 'promo_section_url'
  | 'forum_admins'
  | 'forum_controllers'
  | 'forum_admin_name'
  | 'forum_admin_profile_url'
  | 'name'
>;

type Props = {
  forum: ForumAdminMetaFields;
  darkMode?: boolean;
  className?: string;
};

type PanelKind = 'promo' | 'admins' | 'controllers';

function ContactList({
  items,
  darkMode,
  mute,
  linkCls,
}: {
  items: ForumAdminContact[];
  darkMode: boolean;
  mute: string;
  linkCls: string;
}) {
  return (
    <ul className="space-y-3">
      {items.map((person, i) => (
        <li
          key={`${person.name}-${person.profile_url}-${i}`}
          className={`rounded-lg border px-3 py-2.5 ${
            darkMode ? 'border-gray-600 bg-gray-900/40' : 'border-gray-200 bg-gray-50'
          }`}
        >
          <p className={`text-[10px] uppercase tracking-wide mb-1 ${mute}`}>#{i + 1}</p>
          {person.profile_url ? (
            <a
              href={person.profile_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-start gap-2 break-all text-sm font-medium underline-offset-2 hover:underline ${linkCls}`}
            >
              <ExternalLink size={15} className="shrink-0 mt-0.5" />
              {person.name || person.profile_url}
            </a>
          ) : (
            <span className="text-sm font-medium break-words">{person.name}</span>
          )}
          {person.name && person.profile_url ? (
            <p className={`mt-1 text-xs break-all ${mute}`}>{person.profile_url}</p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/**
 * Kartın sağında kompakt ikon şeridi — yalnızca forumadmin.
 * Kart yüksekliğini büyütmez; tıklanınca sekme’li popup açılır.
 */
const ForumAdminOnlyMeta: React.FC<Props> = ({ forum, darkMode = false, className = '' }) => {
  const { user, loading } = useAuth();
  const [panel, setPanel] = useState<PanelKind | null>(null);
  const titleId = useId();

  useEffect(() => {
    setPanel(null);
  }, [
    forum.promo_section_url,
    forum.forum_admins,
    forum.forum_controllers,
    forum.forum_admin_name,
    forum.forum_admin_profile_url,
  ]);

  if (loading || !isSiteAdmin(user)) return null;

  const promo = forum.promo_section_url?.trim() || '';
  const admins = resolveForumAdmins(forum);
  const controllers = resolveForumControllers(forum);

  const tabs: { id: PanelKind; label: string; icon: typeof Megaphone; available: boolean }[] = [
    { id: 'promo', label: 'Tanıtım', icon: Megaphone, available: Boolean(promo) },
    { id: 'admins', label: 'Admin', icon: UserRound, available: admins.length > 0 },
    { id: 'controllers', label: 'Controller', icon: ShieldCheck, available: controllers.length > 0 },
  ];
  const visibleTabs = tabs.filter((t) => t.available);

  if (visibleTabs.length === 0) return null;

  const openPanel = (id: PanelKind) => setPanel(id);

  const railShell = darkMode
    ? 'border-slate-600 bg-slate-800/95 shadow-lg shadow-black/20'
    : 'border-slate-200 bg-white shadow-md shadow-slate-900/5';
  const iconBtn = darkMode
    ? 'text-slate-300 hover:bg-slate-700 hover:text-white'
    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900';
  const iconBtnActive = darkMode
    ? 'bg-teal-700/40 text-teal-200'
    : 'bg-teal-50 text-teal-800';
  const dialogPanel = darkMode
    ? 'bg-gray-800 text-gray-100 border-gray-600'
    : 'bg-white text-gray-900 border-gray-200';
  const linkCls = darkMode
    ? 'text-sky-300 hover:text-sky-200'
    : 'text-sky-700 hover:text-sky-900';
  const mute = darkMode ? 'text-slate-400' : 'text-slate-500';
  const segmentIdle = darkMode
    ? 'text-slate-400 hover:text-slate-200'
    : 'text-slate-500 hover:text-slate-800';
  const segmentActive = darkMode
    ? 'bg-slate-700 text-white shadow-sm'
    : 'bg-white text-slate-900 shadow-sm';

  const activeTab = panel ? visibleTabs.find((t) => t.id === panel) : null;

  return (
    <>
      {/* Masaüstü: kart yüksekliğine bağlı olmayan ince ikon şeridi */}
      <div
        className={`hidden sm:flex flex-col shrink-0 self-center -ml-1 z-[1] ${className}`}
        aria-label="Yönetim kısayolları"
      >
        <div className={`flex flex-col overflow-hidden rounded-r-xl border border-l-0 ${railShell}`}>
          {visibleTabs.map((tab, index) => {
            const Icon = tab.icon;
            const isActive = panel === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                title={tab.label}
                aria-label={`${tab.label} bilgisini aç`}
                onClick={() => openPanel(tab.id)}
                className={`flex h-9 w-9 items-center justify-center transition-colors ${
                  isActive ? iconBtnActive : iconBtn
                } ${index > 0 ? (darkMode ? 'border-t border-slate-600' : 'border-t border-slate-200') : ''}`}
              >
                <Icon size={15} strokeWidth={2.25} />
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobil: kartın altında tek satır ikonlar */}
      <div
        className={`flex sm:hidden w-full justify-end ${className}`}
        aria-label="Yönetim kısayolları"
      >
        <div className={`inline-flex overflow-hidden rounded-lg border ${railShell}`}>
          {visibleTabs.map((tab, index) => {
            const Icon = tab.icon;
            const isActive = panel === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                title={tab.label}
                aria-label={`${tab.label} bilgisini aç`}
                onClick={() => openPanel(tab.id)}
                className={`flex h-9 w-9 items-center justify-center transition-colors ${
                  isActive ? iconBtnActive : iconBtn
                } ${index > 0 ? (darkMode ? 'border-l border-slate-600' : 'border-l border-slate-200') : ''}`}
              >
                <Icon size={14} strokeWidth={2.25} />
              </button>
            );
          })}
        </div>
      </div>

      <Dialog open={panel !== null} onClose={() => setPanel(null)} className="relative z-[80]">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel
            className={`w-full max-w-md rounded-xl border shadow-xl p-5 ${dialogPanel}`}
            aria-labelledby={titleId}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <Dialog.Title id={titleId} className="text-base font-semibold">
                {activeTab?.label ?? 'Bilgi'}
                {forum.name ? (
                  <span className={`block text-xs font-normal mt-0.5 ${mute}`}>{forum.name}</span>
                ) : null}
              </Dialog.Title>
              <button
                type="button"
                onClick={() => setPanel(null)}
                className={`rounded-lg p-1 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                aria-label="Kapat"
              >
                <X size={18} />
              </button>
            </div>

            {visibleTabs.length > 1 && (
              <div
                className={`mb-4 flex rounded-lg p-0.5 ${
                  darkMode ? 'bg-slate-900/60' : 'bg-slate-100'
                }`}
                role="tablist"
                aria-label="Bölümler"
              >
                {visibleTabs.map((tab) => {
                  const Icon = tab.icon;
                  const selected = panel === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      onClick={() => setPanel(tab.id)}
                      className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                        selected ? segmentActive : segmentIdle
                      }`}
                    >
                      <Icon size={13} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            )}

            {panel === 'promo' && promo && (
              <a
                href={promo}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-start gap-2 break-all text-sm font-medium underline-offset-2 hover:underline ${linkCls}`}
              >
                <ExternalLink size={16} className="shrink-0 mt-0.5" />
                {promo}
              </a>
            )}

            {panel === 'admins' && (
              <ContactList items={admins} darkMode={darkMode} mute={mute} linkCls={linkCls} />
            )}

            {panel === 'controllers' && (
              <ContactList items={controllers} darkMode={darkMode} mute={mute} linkCls={linkCls} />
            )}
          </Dialog.Panel>
        </div>
      </Dialog>
    </>
  );
};

export default ForumAdminOnlyMeta;
